// Отсутствия: отпуска, больничные, командировки, отгулы. ТОЛЬКО ЧТЕНИЕ.
//
// ГЛАВНОЕ ПРО ЭТИ ДАННЫЕ, БЕЗ ЧЕГО ЭКРАН СОВРЁТ
// В графике отсутствий Bitrix лежат не только отсутствия. Диспетчер блокирует человеку
// календарь на время работы по заявке — такая запись выглядит как «Заявка № … судно» и
// имеет тип «другое», и таких записей в графике большинство. Сложить их с отпусками
// значило бы ответить «сегодня отсутствуют столько-то», когда отпусков и больничных среди
// них в разы меньше. Поэтому вид записи считает ядро, отбор по четырём настоящим видам
// стоит по умолчанию, а «работа по заявке» — отдельный вид в фильтре, а не спрятанная строка.
//
// ГРАФИК И ЗАЯВКИ — РАЗНЫЕ УЧЁТЫ, И ЭКРАН ИХ НЕ СКЛАДЫВАЕТ
// Заявку могут отклонить — в графике её тогда нет вовсе; кадровик может внести отпуск в
// график без заявки — тогда нет заявки. Связи между ними в источнике не существует, и
// «тот же человек в те же дни» — догадка, а не связь. Отсюда три вкладки, а не один список.
//
// ЧЕГО ЗДЕСЬ НЕ УВИДЯТ НИКОГДА
// Денег командировки (сумма, аванс, авансовый отчёт, чеки, отметка бухгалтера) и причины
// отсутствия своими словами. Первое — тот же разряд данных, что суточные и ФОТ; второе
// рядом с видом «больничный» превращается в сведение о здоровье человека. Ядро их не
// отдаёт вовсе, а не прячет на экране.
//
// ОТСЮДА НЕЛЬЗЯ НИ ОФОРМИТЬ ОТПУСК, НИ СОГЛАСОВАТЬ ЗАЯВКУ
// На этих списках Bitrix висят живые бизнес-процессы: запись в элемент ставит задачу
// живому человеку. Ручки записи нет и в ядре.
import { useMemo, useState } from 'react'

import {
  ИМЯ_СУЩНОСТИ,
  ОГОВОРКИ_ОТСУТСТВИЙ,
  РАЗМЕР_СТРАНИЦЫ,
  useЗаявкиНаОтпуск,
  useКомандировки,
  useОтсутствия,
  useСправочникиОтсутствий,
  самаяСтарая,
} from '../api/absence'
import type {
  ЗаявкаСтрока,
  КомандировкаСтрока,
  ОтсутствиеСтрока,
  СправочникиОтсутствий,
} from '../api/absence'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, pluralN, todayIso } from '../lib/format'
import { БЕЗ_РЕШЕНИЯ, классВида, классРешения, поВидам } from '../lib/absence'

type Вкладка = 'graph' | 'requests' | 'trips'

export default function Absence() {
  const [вкладка, setВкладка] = useState<Вкладка>('graph')
  const [день, setДень] = useState(todayIso())
  const refs = useСправочникиОтсутствий(день)

  const св = самаяСтарая(refs.data?.freshness)

  return (
    <>
      <div className="page-head">
        <h1>Отсутствия</h1>
        <span className="spacer" />
        <span className="hint">
          {св
            ? `обновлено ${(св.age_min ?? 0) < 1 ? 'только что' : `${Math.round(св.age_min ?? 0)} мин назад`}`
              + ` (позже всех — ${ИМЯ_СУЩНОСТИ[св.entity] ?? св.entity})`
            : 'свежесть неизвестна'}
        </span>
      </div>

      <div className="notice" style={{ marginBottom: 12 }}>
        <b>Отсюда нельзя ни оформить отпуск, ни согласовать заявку.</b>{' '}
        Работают в этом в Bitrix, он и остаётся источником правды: на этих списках висят
        бизнес-процессы, которые ставят задачи живым людям. Денег командировки и причины
        отсутствия своими словами здесь нет вовсе — их не отдаёт ядро.
      </div>

      <ДеньИСвод refs={refs.data} день={день} setДень={setДень} />

      {/* Переключатель вкладок — обычный flex, а НЕ .filters: на телефоне правило
          «.filters > * { width: 100% }» поставило бы кнопки в столбец. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {([
          ['graph', подписьВкладки('График', refs.data?.counts?.graph_rows)],
          ['requests', подписьВкладки('Заявки на отпуск и отгул', refs.data?.counts?.requests)],
          ['trips', подписьВкладки('Командировки', refs.data?.counts?.trips)],
        ] as [Вкладка, string][]).map(([код, подпись]) => (
          <button key={код} onClick={() => setВкладка(код)}
                  aria-pressed={вкладка === код}
                  style={{ fontWeight: вкладка === код ? 600 : 400, flex: '0 1 auto' }}>
            {подпись}
          </button>
        ))}
      </div>

      <QueryState isLoading={refs.isLoading} error={refs.error}>
        {вкладка === 'graph' && <График refs={refs.data} день={день} />}
        {вкладка === 'requests' && <Заявки refs={refs.data} />}
        {вкладка === 'trips' && <Командировки refs={refs.data} />}
        <Оговорки заметки={refs.data?.data_notes} />
      </QueryState>
    </>
  )
}

/** Число рядом с названием вкладки — из ядра. Нет числа — нет и обещания его знать. */
function подписьВкладки(имя: string, сколько?: number | string | null): string {
  return сколько === undefined || сколько === null ? имя : `${имя} · ${сколько}`
}

/**
 * «Кто отсутствует в этот день» — то, ради чего раздел открывают чаще всего.
 *
 * Свод считает ЯДРО тем же отбором, что и таблицы ниже. Складывать его на экране из
 * видимых строк нельзя: на странице пятьдесят строк из нескольких сотен, и «в отпуске
 * столько-то» получилось бы ответом на другой вопрос.
 */
function ДеньИСвод({ refs, день, setДень }: {
  refs?: СправочникиОтсутствий; день: string; setДень: (v: string) => void
}) {
  const своды = поВидам(refs?.on_date, refs?.absence_kinds ?? [])
  const настоящие = своды.filter((с) => (refs?.absence_kinds ?? []).includes(с.kind))
  // Итог берём готовым из ядра, а не складываем плитки: у человека в один день могут
  // стоять и командировка, и отгул — сумма по видам посчитала бы его дважды.
  const людей = refs?.on_total?.people_absent ?? null
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <FilterBar id="absence-day">
        <label htmlFor="absence-day-input">
          Кто отсутствует в день
          <input id="absence-day-input" type="date" value={день}
                 onChange={(e) => setДень(e.target.value || todayIso())} />
        </label>
      </FilterBar>
      <div className="tiles" style={{ marginTop: 8 }}>
        {настоящие.length === 0 && (
          <div className="tile">
            <div className="t-label">в этот день</div>
            <div className="t-sum">—</div>
            <div className="t-label">отпусков, больничных, командировок и отгулов нет</div>
          </div>
        )}
        {настоящие.map((с) => (
          <div className="tile" key={с.kind}>
            <div className="t-label">{с.kind}</div>
            <div className="t-sum">{с.people}</div>
            <div className="t-label">
              {pluralN(с.rows, 'запись', 'записи', 'записей')} в графике
            </div>
          </div>
        ))}
      </div>
      <div className="hint" style={{ marginTop: 8 }}>
        Считает ядро тем же отбором, что и таблицы ниже. Один человек может стоять в
        нескольких записях сразу — поэтому итог ниже это РАЗНЫЕ люди, а не сумма плиток.
        {людей !== null && ` Всего в этот день не на работе: ${людей}.`}
      </div>
    </div>
  )
}

/** Оговорки из ядра. Тексты и числа в них считает то же ядро, что и данные рядом. */
function Оговорки({ заметки }: { заметки?: Record<string, string> }) {
  if (!заметки) return null
  const важные = ОГОВОРКИ_ОТСУТСТВИЙ.filter((к) => заметки[к])
  if (!важные.length) return null
  return (
    <div className="card hint" style={{ marginTop: 12 }}>
      <b>Что нужно знать об этих данных</b>
      <ul className="plain-list" style={{ marginTop: 6 }}>
        {важные.map((к) => <li key={к}>{заметки[к]}</li>)}
      </ul>
    </div>
  )
}

function Страницы({ всего, start, setStart }: {
  всего: number; start: number; setStart: (v: number) => void
}) {
  if (всего <= РАЗМЕР_СТРАНИЦЫ) return null
  return (
    <div className="pager">
      <button disabled={start === 0}
              onClick={() => setStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}>
        назад
      </button>
      <span className="muted">
        {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, всего)} из {всего}
      </span>
      <button disabled={start + РАЗМЕР_СТРАНИЦЫ >= всего}
              onClick={() => setStart(start + РАЗМЕР_СТРАНИЦЫ)}>
        дальше
      </button>
    </div>
  )
}

/** Период одной строкой. Даты приезжают днём без времени — время у них не заполняют. */
function Период({ с, по, битый }: { с?: string | null; по?: string | null; битый?: boolean }) {
  if (!с && !по) return <span className="muted">даты нет</span>
  return (
    <span className={битый ? 'badge lose' : undefined}>
      {fmtDate(с)} — {fmtDate(по)}
      {битый && ' (окончание раньше начала)'}
    </span>
  )
}

// ═══════════════════════════════ график ═══════════════════════════════
//
// По умолчанию показаны ЧЕТЫРЕ вида — отпуск, больничный, командировка, отгул. Это не
// «спрятать неудобное»: блокировка календаря под заявку не является отсутствием, и список
// вперемешку отвечал бы на вопрос «в какие дни у кого закрыт календарь», а спрашивают его
// о другом. Вид «работа по заявке» стоит в том же фильтре — снять отбор можно в один клик.

function График({ refs, день }: { refs?: СправочникиОтсутствий; день: string }) {
  const виды = refs?.absence_kinds ?? []
  const [вид, setВид] = useState('')
  const [человек, setЧеловек] = useState('')
  const [отдел, setОтдел] = useState('')
  const [с, setС] = useState('')
  const [по, setПо] = useState('')
  const [наДень, setНаДень] = useState(false)
  const [поиск, setПоиск] = useState('')
  const [сортировка, setСортировка] = useState('start')
  const [start, setStart] = useState(0)

  const фильтр = useMemo(() => ({
    kind: вид || виды.join(','),
    user: человек,
    dept: отдел,
    from: с,
    to: по,
    on: наДень ? день : '',
    q: поиск,
    sort: сортировка,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [вид, виды, человек, отдел, с, по, наДень, день, поиск, сортировка, start])

  const запрос = useОтсутствия(фильтр)
  const строки = запрос.data?.rows ?? []
  const итоги = запрос.data?.totals

  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }

  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <FilterBar id="absence-graph">
          <label htmlFor="absence-kind">
            Вид
            <select id="absence-kind" value={вид}
                    onChange={(e) => сСброса(setВид)(e.target.value)}>
              <option value="">отпуск, больничный, командировка, отгул</option>
              {(refs?.kinds ?? []).map((к) => (
                <option key={к} value={к}>{к}</option>
              ))}
            </select>
          </label>
          <label htmlFor="absence-person">
            Сотрудник
            <select id="absence-person" value={человек}
                    onChange={(e) => сСброса(setЧеловек)(e.target.value)}>
              <option value="">все</option>
              {(refs?.people ?? []).map((ч) => (
                <option key={ч.bitrix_id} value={String(ч.bitrix_id)}>
                  {ч.fio ?? String(ч.bitrix_id)}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="absence-dept">
            Подразделение
            <select id="absence-dept" value={отдел}
                    onChange={(e) => сСброса(setОтдел)(e.target.value)}>
              <option value="">все</option>
              {(refs?.departments ?? []).map((о) => (
                <option key={о.bitrix_id} value={String(о.bitrix_id)}>{о.name}</option>
              ))}
            </select>
          </label>
          <label htmlFor="absence-from">
            Пересекается с
            <input id="absence-from" type="date" value={с}
                   onChange={(e) => сСброса(setС)(e.target.value)} />
          </label>
          <label htmlFor="absence-to">
            по
            <input id="absence-to" type="date" value={по}
                   onChange={(e) => сСброса(setПо)(e.target.value)} />
          </label>
          <label htmlFor="absence-sort">
            Порядок
            <select id="absence-sort" value={сортировка}
                    onChange={(e) => сСброса(setСортировка)(e.target.value)}>
              <option value="start">по началу</option>
              <option value="end">по окончанию</option>
              <option value="fio">по фамилии</option>
              <option value="days">по длительности</option>
            </select>
          </label>
          <label htmlFor="absence-q">
            Поиск
            <input id="absence-q" type="search" value={поиск}
                   placeholder="фамилия, должность, отдел, причина"
                   onChange={(e) => сСброса(setПоиск)(e.target.value)} />
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={наДень}
                   onChange={(e) => сСброса(setНаДень)(e.target.checked)} />
            только идущие в выбранный день
          </label>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          найдено: {запрос.data?.total ?? 0}
          {итоги
            ? ` · ${pluralN(итоги.people ?? 0, 'человек', 'человека', 'человек')}`
              + ` · ${pluralN(итоги.days ?? 0, 'день', 'дня', 'дней')} суммарно`
              + ((итоги.bad_period ?? 0) > 0
                ? ` · у ${итоги.bad_period} окончание раньше начала`
                : '')
            : ''}
        </div>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <ТаблицаГрафика строки={строки} />
        <Страницы всего={запрос.data?.total ?? 0} start={start} setStart={setStart} />
      </QueryState>
    </>
  )
}

function ТаблицаГрафика({ строки }: { строки: ОтсутствиеСтрока[] }) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">Под этот отбор в графике ничего нет</div>
        <div className="empty-hint">
          Снимите отбор по виду: кроме отпусков, больничных, командировок и отгулов в
          графике Bitrix лежат блокировки календаря под работу по заявке — они показаны
          отдельным видом. Старых записей в зеркале может не быть вовсе: синк списков
          берёт элементы с отсечки, и её день назван в оговорках ниже.
        </div>
      </div>
    )
  }
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th>Сотрудник</th>
            <th>Подразделение</th>
            <th>Вид</th>
            <th>Период</th>
            <th className="num">Дней</th>
            <th>Запись в графике</th>
          </tr>
        </thead>
        <tbody>
          {строки.map((r) => (
            <tr key={r.element_id}>
              <td data-label="Сотрудник">
                {r.fio ?? <span className="muted">не сопоставлен</span>}
                {r.position && <div className="hint">{r.position}</div>}
              </td>
              <td data-label="Подразделение">
                {r.department ?? <span className="muted">—</span>}
              </td>
              <td data-label="Вид">
                <span className={классВида(r.kind)}>{r.kind}</span>
                {r.type_name && r.type_name !== r.kind && (
                  <div className="hint">{r.type_name}</div>
                )}
              </td>
              <td data-label="Период">
                <Период с={r.date_from} по={r.date_to} битый={r.bad_period} />
              </td>
              <td className="num" data-label="Дней">
                {r.days ?? <span className="muted">—</span>}
              </td>
              <td data-label="Запись в графике">
                {r.reason ?? <span className="muted">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ═══════════════════════ заявки на отпуск и отгул ═══════════════════════
//
// Это НЕ график: заявку могут отклонить, и тогда отпуска не было. Отметка кадров показана
// значком, а её отсутствие — словом «без отметки», а не пустой клеткой: пустое место
// читается как «отказали» ровно так же часто, как «ещё не смотрели», а это противоположные
// вещи. Человек берётся из названия записи, а не из автора — за кого-то заявку заводят
// кадры, и такие строки помечены.

function Заявки({ refs }: { refs?: СправочникиОтсутствий }) {
  const [видЗаявки, setВидЗаявки] = useState('')
  const [отметка, setОтметка] = useState('')
  const [человек, setЧеловек] = useState('')
  const [поиск, setПоиск] = useState('')
  const [сортировка, setСортировка] = useState('start')
  const [start, setStart] = useState(0)

  const фильтр = useMemo(() => ({
    type: видЗаявки,
    hr: отметка,
    user: человек,
    q: поиск,
    sort: сортировка,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [видЗаявки, отметка, человек, поиск, сортировка, start])

  const запрос = useЗаявкиНаОтпуск(фильтр)
  const строки = запрос.data?.rows ?? []
  const итоги = запрос.data?.totals

  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }

  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <FilterBar id="absence-requests">
          <label htmlFor="absence-req-kind">
            Вид заявки
            <select id="absence-req-kind" value={видЗаявки}
                    onChange={(e) => сСброса(setВидЗаявки)(e.target.value)}>
              <option value="">все</option>
              {(refs?.request_kinds ?? []).map((з) => (
                <option key={String(з.enum_id)} value={String(з.enum_id ?? '')}>
                  {з.enum_text}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="absence-req-hr">
            Отметка кадров
            <select id="absence-req-hr" value={отметка}
                    onChange={(e) => сСброса(setОтметка)(e.target.value)}>
              <option value="">любая</option>
              {(refs?.hr_marks ?? []).map((з) => (
                <option key={String(з.enum_id)} value={String(з.enum_id ?? '')}>
                  {з.enum_text}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="absence-req-person">
            Сотрудник
            <select id="absence-req-person" value={человек}
                    onChange={(e) => сСброса(setЧеловек)(e.target.value)}>
              <option value="">все</option>
              {(refs?.people ?? []).map((ч) => (
                <option key={ч.bitrix_id} value={String(ч.bitrix_id)}>
                  {ч.fio ?? String(ч.bitrix_id)}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="absence-req-sort">
            Порядок
            <select id="absence-req-sort" value={сортировка}
                    onChange={(e) => сСброса(setСортировка)(e.target.value)}>
              <option value="start">по началу</option>
              <option value="end">по окончанию</option>
              <option value="new">по номеру записи</option>
              <option value="fio">по фамилии</option>
            </select>
          </label>
          <label htmlFor="absence-req-q">
            Поиск
            <input id="absence-req-q" type="search" value={поиск}
                   placeholder="фамилия, юрлицо"
                   onChange={(e) => сСброса(setПоиск)(e.target.value)} />
          </label>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          найдено: {запрос.data?.total ?? 0}
          {итоги
            ? ` · согласовано ${итоги.approved ?? 0}`
              + ` · отклонено ${итоги.declined ?? 0}`
              + ` · без отметки кадров ${итоги.no_hr_mark ?? 0}`
            : ''}
        </div>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <ТаблицаЗаявок строки={строки} />
        <Страницы всего={запрос.data?.total ?? 0} start={start} setStart={setStart} />
      </QueryState>
    </>
  )
}

function ТаблицаЗаявок({ строки }: { строки: ЗаявкаСтрока[] }) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">Заявок под этот отбор нет</div>
        <div className="empty-hint">
          Заявку подают в Bitrix, в зеркало она приезжает ближайшим прогоном. Старых заявок
          в зеркале может не быть вовсе: синк списков берёт элементы с отсечки, и её день
          назван в оговорках ниже. Отсутствие заявки не значит, что отпуска не было, —
          график ведут отдельно, на соседней вкладке.
        </div>
      </div>
    )
  }
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th>Сотрудник</th>
            <th>Вид заявки</th>
            <th>Период</th>
            <th className="num">Дней</th>
            <th>Отметка кадров</th>
            <th>Замена и юрлицо</th>
          </tr>
        </thead>
        <tbody>
          {строки.map((r) => (
            <tr key={r.element_id}>
              <td data-label="Сотрудник">
                {r.fio ?? <span className="muted">не сопоставлен</span>}
                {r.by_author && (
                  <div className="hint">
                    заявку завёл {r.author_fio ?? 'другой сотрудник'} — человек взят из
                    автора записи, а не из её названия
                  </div>
                )}
              </td>
              <td data-label="Вид заявки">
                {r.request_kind ?? <span className="muted">—</span>}
              </td>
              <td data-label="Период">
                <Период с={r.date_from} по={r.date_to} битый={r.bad_period} />
              </td>
              <td className="num" data-label="Дней">
                {r.days ?? <span className="muted">—</span>}
              </td>
              <td data-label="Отметка кадров">
                <span className={классРешения(r.hr_status)}>
                  {r.hr_status ?? БЕЗ_РЕШЕНИЯ}
                </span>
              </td>
              <td data-label="Замена и юрлицо">
                {r.standin_fio ?? <span className="muted">замена не указана</span>}
                {r.company && <div className="hint">{r.company}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ═══════════════════════════ командировки ═══════════════════════════
//
// В том же списке Bitrix лежат запросы денег под отчёт — сюда они не попадают вовсе: это
// платёж, а не отсутствие. Отбор делает ядро по виду заявки, а не экран фильтром: иначе
// достаточно было бы снять галочку, чтобы раздел «Отсутствия» показал денежный документ.

function Командировки({ refs }: { refs?: СправочникиОтсутствий }) {
  const [видПоездки, setВидПоездки] = useState('')
  const [статус, setСтатус] = useState('')
  const [человек, setЧеловек] = useState('')
  const [поиск, setПоиск] = useState('')
  const [сортировка, setСортировка] = useState('start')
  const [start, setStart] = useState(0)

  const фильтр = useMemo(() => ({
    type: видПоездки,
    status: статус,
    user: человек,
    q: поиск,
    sort: сортировка,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [видПоездки, статус, человек, поиск, сортировка, start])

  const запрос = useКомандировки(фильтр)
  const строки = запрос.data?.rows ?? []
  const итоги = запрос.data?.totals

  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }

  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <FilterBar id="absence-trips">
          <label htmlFor="absence-trip-kind">
            Вид командировки
            <select id="absence-trip-kind" value={видПоездки}
                    onChange={(e) => сСброса(setВидПоездки)(e.target.value)}>
              <option value="">все</option>
              {(refs?.trip_kinds ?? []).map((з) => (
                <option key={String(з.enum_id)} value={String(з.enum_id ?? '')}>
                  {з.enum_text}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="absence-trip-status">
            Решение
            <select id="absence-trip-status" value={статус}
                    onChange={(e) => сСброса(setСтатус)(e.target.value)}>
              <option value="">любое</option>
              {(refs?.trip_statuses ?? []).map((з) => (
                <option key={String(з.enum_id)} value={String(з.enum_id ?? '')}>
                  {з.enum_text}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="absence-trip-person">
            Кто едет
            <select id="absence-trip-person" value={человек}
                    onChange={(e) => сСброса(setЧеловек)(e.target.value)}>
              <option value="">все</option>
              {(refs?.people ?? []).map((ч) => (
                <option key={ч.bitrix_id} value={String(ч.bitrix_id)}>
                  {ч.fio ?? String(ч.bitrix_id)}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="absence-trip-sort">
            Порядок
            <select id="absence-trip-sort" value={сортировка}
                    onChange={(e) => сСброса(setСортировка)(e.target.value)}>
              <option value="start">по началу</option>
              <option value="end">по окончанию</option>
              <option value="new">по номеру записи</option>
              <option value="place">по месту</option>
            </select>
          </label>
          <label htmlFor="absence-trip-q">
            Поиск
            <input id="absence-trip-q" type="search" value={поиск}
                   placeholder="фамилия, город, цель, судно"
                   onChange={(e) => сСброса(setПоиск)(e.target.value)} />
          </label>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          найдено: {запрос.data?.total ?? 0}
          {итоги
            ? ` · согласовано ${итоги.approved ?? 0}`
              + ` · отказано ${итоги.declined ?? 0}`
              + ` · без решения ${итоги.no_status ?? 0}`
              + ((итоги.no_person ?? 0) > 0
                ? ` · без указанного сотрудника ${итоги.no_person}`
                : '')
            : ''}
        </div>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <ТаблицаКомандировок строки={строки} />
        <Страницы всего={запрос.data?.total ?? 0} start={start} setStart={setStart} />
      </QueryState>
    </>
  )
}

function ТаблицаКомандировок({ строки }: { строки: КомандировкаСтрока[] }) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">Командировок под этот отбор нет</div>
        <div className="empty-hint">
          Заявку на командировку оформляют в Bitrix. Запросы денег под отчёт лежат в том же
          списке источника, но сюда не попадают вовсе — это платёж, а не отсутствие, и его
          место в денежном разделе. Старых заявок в зеркале может не быть: синк списков
          берёт элементы с отсечки, и её день назван в оговорках ниже.
        </div>
      </div>
    )
  }
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th>Кто едет</th>
            <th>Куда и зачем</th>
            <th>Период</th>
            <th className="num">Дней</th>
            <th>Решение</th>
            <th>Кто отправил</th>
          </tr>
        </thead>
        <tbody>
          {строки.map((r) => (
            <tr key={r.element_id}>
              <td data-label="Кто едет">
                {r.fio ?? <span className="muted">в заявке не указан</span>}
                {!r.fio && r.name_in_source && (
                  <div className="hint">в источнике записано: {r.name_in_source}</div>
                )}
              </td>
              <td data-label="Куда и зачем">
                {r.place ?? <span className="muted">место не указано</span>}
                {r.purpose && <div className="hint">{r.purpose}</div>}
                {r.vessel_ref && <div className="hint">судно: {r.vessel_ref}</div>}
              </td>
              <td data-label="Период">
                <Период с={r.date_from} по={r.date_to} битый={r.bad_period} />
              </td>
              <td className="num" data-label="Дней">
                {r.days ?? <span className="muted">—</span>}
              </td>
              <td data-label="Решение">
                <span className={классРешения(r.status)}>{r.status ?? БЕЗ_РЕШЕНИЯ}</span>
                {r.trip_kind && <div className="hint">{r.trip_kind}</div>}
              </td>
              <td data-label="Кто отправил">
                {r.sender_fio ?? <span className="muted">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
